import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { AppError } from '@shared/api/error'
import { useEffect, useState } from 'react'
import {
  adminKycVerificationKey,
  useAdminKycVerification,
} from '@entities/admin-user/api/kyc-verification'
import { RESERVE_CONFIRMATION_TIMEOUT_MS, reserveSyncState } from '../model/reserve-sync'
import {
  ReceivingPlansResponseSchema,
  DefaultReceivingPlanSchema,
  type UserReceivingPlan,
} from '../model/user-receiving-plan'

const path = (userId: string) => `/admin/users/${encodeURIComponent(userId)}/receiving-plan`
export function useAdminReserveSync(userId: string, plan: UserReceivingPlan) {
  const [confirmationTimedOut, setConfirmationTimedOut] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setConfirmationTimedOut(true), RESERVE_CONFIRMATION_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])
  const query = useAdminKycVerification(userId, true, {
    refetchInterval: (query) =>
      !confirmationTimedOut &&
      query.state.status !== 'error' &&
      query.state.data &&
      reserveSyncState(plan, query.state.data) === 'pending'
        ? 5000
        : false,
  })
  return { ...query, confirmationTimedOut }
}
export function useAdminReceivingPlan(userId: string) {
  return useQuery({
    queryKey: ['admin-receiving-plan', userId],
    queryFn: async () => ReceivingPlansResponseSchema.parse(await apiFetch(path(userId))),
    staleTime: 0,
  })
}
export function useUpdateAdminReceivingPlan(userId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      plan,
      reason,
      expectedVersionId,
      expectedDefaultVersionId,
    }: {
      plan: UserReceivingPlan
      reason: string
      expectedVersionId: string | null
      expectedDefaultVersionId: string | null
    }) => {
      const body = {
        reason,
        expectedVersionId,
        ...(plan.kind === 'CUSTOM' ? { expectedDefaultVersionId } : {}),
        ...(plan.kind === 'STANDARD'
          ? { kind: 'STANDARD', receivingPeriod: plan.receivingPeriod }
          : {
              kind: 'CUSTOM',
              ...(plan.serviceFee ? { serviceFee: plan.serviceFee } : {}),
              receivingDays: plan.receivingDays,
              withdrawalFeeBrl: plan.withdrawalFeeBrl,
              withdrawalFees: plan.withdrawalFees ?? {},
              fees: plan.fees,
            }),
      }
      return ReceivingPlansResponseSchema.parse(
        await apiFetch(path(userId), { method: 'PUT', body }),
      )
    },
    onSuccess: (data) => {
      client.setQueryData(['admin-receiving-plan', userId], data)
      void client.invalidateQueries({ queryKey: adminKycVerificationKey(userId) })
    },
    onError: async (error) => {
      if (error instanceof AppError && error.status === 409)
        await client.refetchQueries({ queryKey: ['admin-receiving-plan', userId] })
    },
  })
}

const defaultPath = '/admin/users/receiving-plan-default'

export function useDefaultReceivingPlan() {
  return useQuery({
    queryKey: ['default-receiving-plan'],
    queryFn: async () => DefaultReceivingPlanSchema.parse(await apiFetch(defaultPath)),
    staleTime: 0,
  })
}

export function useUpdateDefaultReceivingPlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      plan,
      reason,
      expectedVersionId,
    }: {
      plan: UserReceivingPlan
      reason: string
      expectedVersionId: string | null
    }) =>
      DefaultReceivingPlanSchema.parse(
        await apiFetch(defaultPath, {
          method: 'PUT',
          body: { reason, expectedVersionId, fees: plan.fees, withdrawalFees: plan.withdrawalFees },
        }),
      ),
    onSuccess: (data) => {
      client.setQueryData(['default-receiving-plan'], data)
      void client.invalidateQueries({ queryKey: ['admin-receiving-plan'] })
      void client.invalidateQueries({ queryKey: ['admin-kyc-verification'] })
    },
    onError: async (error) => {
      if (error instanceof AppError && error.status === 409)
        await client.refetchQueries({ queryKey: ['default-receiving-plan'] })
    },
  })
}
